import * as React from 'react';
import { useState } from 'react';
import { Link } from '@inertiajs/react';
import { User, UserAddress, DeliveryCheckResult, DeliveryScheduleEntry } from '../../types/landing';
import { DeliveryCheckModal } from './DeliveryCheckModal';

interface DeliveryZoneSectionProps {
  user: User | null;
  userAddresses: UserAddress[];
  deliverySchedule: DeliveryScheduleEntry[];
}

export function DeliveryZoneSection({ user, userAddresses, deliverySchedule }: DeliveryZoneSectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [checkResult, setCheckResult] = useState<DeliveryCheckResult | null>(null);

  const hasAddresses = userAddresses && userAddresses.length > 0;
  const deliverableAddresses = userAddresses?.filter(a => a.isDeliverable && a.nextDelivery) || [];

  return (
    <section id="zonas" className="bg-gradient-to-br from-amber-50 to-amber-100 py-10 border-t border-b border-black/5 md:py-8">
      <div className="max-w-[1200px] mx-auto px-5">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-xl font-bold text-gray-800 m-0 mb-2 flex items-center justify-center gap-2.5">
            <span className="text-[28px]"><i className="fa fa-truck"></i></span>
            {user && hasAddresses ? 'Tus direcciones de entrega' : '¿Llegamos a tu domicilio?'}
          </h2>
          <p className="text-base md:text-sm text-gray-500 m-0">
            {user && hasAddresses
              ? 'Estas son las fechas de entrega disponibles para tus direcciones'
              : 'Verificá si hacemos entregas en tu zona'}
          </p>
        </div>

        {/* User addresses */}
        {user && deliverableAddresses.length > 0 && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4 mb-6 max-md:grid-cols-1">
            {deliverableAddresses.map((addr) => (
              <div
                key={addr.id}
                className={`bg-white rounded-xl p-4 shadow-sm ${
                  addr.isDeliverable ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500 opacity-70'
                }`}
              >
                <div className="text-[15px] font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <i className={`fa fa-map-marker ${addr.isDeliverable ? 'text-green-500' : 'text-red-500'}`}></i>
                  {addr.address}
                </div>
                {addr.isDeliverable && addr.nextDelivery ? (
                  <>
                    <div className="text-[13px] text-gray-500 mb-1">Comprando ahora:</div>
                    <div className="text-sm font-medium text-green-500">
                      <i className="fa fa-calendar mr-1.5"></i>
                      {addr.nextDelivery.label} ({addr.nextDelivery.timeRange})
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-red-500">
                    <i className="fa fa-times-circle mr-1.5"></i>
                    No hacemos entregas en esta dirección
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Delivery schedule table */}
        {deliverySchedule.length > 0 && deliverableAddresses.length === 0 && (
          <div className="max-w-lg mx-auto mb-8">
            <h3 className="text-base font-semibold text-gray-700 mb-3 text-center">
              <i className="fa fa-calendar-o mr-1.5"></i>
              Próximas entregas programadas
            </h3>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {deliverySchedule.map((entry, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between px-4 py-3 ${
                    i !== deliverySchedule.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-50 text-green-600 text-xs font-bold shrink-0">
                      <i className="fa fa-map-marker"></i>
                    </span>
                    <span className="text-sm font-semibold text-gray-800">{entry.zone_title}</span>
                  </div>
                  <div className="text-sm text-gray-600 text-right">
                    <span className="capitalize">{entry.day_label}</span>
                    <span className="text-gray-400 mx-1.5">·</span>
                    <span className="font-medium text-green-600">{entry.time_range}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA Button */}
        <div className="text-center">
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center gap-2.5 px-7 py-3.5 text-base font-semibold text-white bg-gradient-to-br from-green-500 to-green-600 rounded-lg cursor-pointer transition-all shadow-[0_4px_12px_rgba(34,197,94,0.3)] hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(34,197,94,0.4)] active:translate-y-0 border-none max-md:w-full max-md:justify-center max-md:px-6 max-md:py-4"
          >
            <i className="fa fa-search text-lg"></i>
            {user && hasAddresses ? 'Verificar otra dirección' : 'Comprobá cuándo podemos entregarte'}
          </button>
          <div className="mt-4">
            <Link href="/delivery" className="text-sm font-semibold text-green-700 underline hover:text-green-800">
              Mirá las zonas, el costo de envío y el pedido mínimo
            </Link>
          </div>
        </div>

        {/* Modal */}
        {isModalOpen && (
          <DeliveryCheckModal
            user={user}
            onClose={() => { setIsModalOpen(false); setCheckResult(null); }}
            onResult={setCheckResult}
            result={checkResult}
            showMap={false}
          />
        )}
      </div>
    </section>
  );
}
